/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';
import {WINDOW} from '../../app/angular-platform-providers';

const DEFAULT_HOME_STATE = 'app.home';
const DEFAULT_NOT_FOUND_STATE = 'app.errors.404';

function stateName(stateOrName: any): string | undefined {
  return _.isString(stateOrName) ? stateOrName : _.get(stateOrName, 'name');
}

function isSignInDestination(dest: string): boolean {
  return !dest || dest === '/sign-in' || _.startsWith(dest, '/sign-in/') || _.startsWith(dest, '/sign-in?');
}

function getInternalDestination(window: any, dest: any): string | null {
  if (!_.isString(dest) || !dest) {
    return null;
  }
  try {
    const currentUrl = new URL(window.location.href || window.location.origin);
    const destination = new URL(dest, currentUrl);
    if (destination.origin !== currentUrl.origin) {
      return null;
    }
    return destination.pathname + destination.search + destination.hash;
  } catch (_error) {
    return null;
  }
}

function fallbackNavigation($state: any, homeState: string, notFoundState: string): any {
  return $state.go(notFoundState, null, {inherit: false, location: false})
    .catch(function() {
      return $state.go(homeState, null, {inherit: false});
    });
}

@Injectable({
  providedIn: 'root'
})
export class AdnLoginNavigationService {
  constructor(
    @Inject(WINDOW) private readonly window: any
  ) {
  }

  navigateAfterLogin($state: any, location: any, $urlRouter: any, options: any): any {
    const dest = getInternalDestination(this.window, options.dest);
    const homeState = options.homeState || DEFAULT_HOME_STATE;
    const notFoundState = options.notFoundState || DEFAULT_NOT_FOUND_STATE;
    const onSuccess = options.onSuccess || (() => undefined);
    const fallback = () => fallbackNavigation($state, homeState, notFoundState);

    // Keep post-login navigation in the current Angular runtime. A hard browser
    // navigation reloads and re-executes every initial webpack chunk.
    if (dest && !isSignInDestination(dest)) {
      location.navigate(dest);
      $urlRouter.sync();
      return Promise.resolve().then(() => onSuccess());
    }

    return $state.go(homeState, null, {inherit: false}).then(onSuccess, function() {
      return fallback().then(onSuccess);
    });
  }

  navigateToStateOrHome($state: any, toState: any, toParams: any, options?: any): any {
    const homeState = (options && options.homeState) || DEFAULT_HOME_STATE;
    const state = stateName(toState);
    if (!state) {
      return $state.go(homeState, null, {inherit: false});
    }
    return $state.go(state, toParams, {inherit: false}).catch(function() {
      return $state.go(homeState, null, {inherit: false});
    });
  }
}
